import { useState, useEffect } from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

interface ProductOverviewProps {
  product: any;
}

export default function ProductOverview({ product }: ProductOverviewProps) {
  const { ref, isVisible } = useScrollAnimation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!product?.images || product.images.length <= 1 || !isAutoPlaying)
      return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [product?.images, isAutoPlaying]);

  const nextImage = () => {
    if (!product?.images) return;
    setCurrentImageIndex((prev) =>
      prev === product.images.length - 1 ? 0 : prev + 1
    );
    setIsAutoPlaying(false);
  };

  const prevImage = () => {
    if (!product?.images) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1
    );
    setIsAutoPlaying(false);
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <section ref={ref} id="overview" className="py-12 bg-white">
      <div
        className={`max-w-4xl mx-auto px-6 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-20">
          <div className="relative">
            <div className="h-60 rounded-3xl overflow-hidden justify-center flex">
              {product.images && product.images.length > 0 ? (
                <>
                  <img
                    src={product.images[currentImageIndex]}
                    alt={`${product.title} ${currentImageIndex + 1}`}
                    className="w-96 h-60 object-cover transition-transform duration-500 ease-in-out rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.parentElement?.classList.add(
                        "flex",
                        "items-center",
                        "justify-center"
                      );
                      const fallback = document.createElement("div");
                      fallback.className =
                        "text-white text-2xl font-bold text-center p-8";
                      fallback.innerHTML = `<div class="text-4xl mb-4">🔋</div><div>${product.title}</div>`;
                      e.currentTarget.parentElement?.appendChild(fallback);
                    }}
                  />
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="text-6xl mb-4">🔋</div>
                    <div className="text-2xl font-bold">{product.title}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 lg:w-[470px] w-[300px] mx-auto">
            <div>
              <h3 className="text-3xl font-light text-sky-500 mb-2">
                {product.title}
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
